import { useState, useMemo, useEffect } from "react";
import axios from "axios";
import { Users, Search, Trash2, Edit2, ChevronLeft, ChevronRight, Save, X } from "lucide-react";

const GroupContactManager = () => {
  // --- State Hooks for API Data Streams ---
  const [tables, setTables] = useState([]);
  const [groups, setGroups] = useState([]);
  const [subgroups, setSubgroups] = useState([]);
  const [contacts, setContacts] = useState([]);

  // Filter States
  const [selectedTable, setSelectedTable] = useState("");
  const [selectedGroup, setSelectedGroup] = useState("");
  const [selectedSubgroup, setSelectedSubgroup] = useState("");
  const [searchTerm, setSearchTerm] = useState("");

  // Pagination States
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;

  // Dedicated Form Editing States
  const [editingContact, setEditingContact] = useState(null);
  const [editName, setEditName] = useState("");
  const [editMobile, setEditMobile] = useState("");
  const [editExtraFields, setEditExtraFields] = useState({});

  const excludedKeys = [
    'id', 'name', 'mobile', 'group_id', 'subgroup_id',
    'member_id', 'role', 'flag', 'user_id', 'group_name',
    'subgroup_name', 'table_name', 'table_source',
    'group_id_matched', 'subgroup_id_matched'
  ];

  const [selectedContacts, setSelectedContacts] = useState([]);
  const handleCheckbox = (id) => {
    setSelectedContacts(prev =>
      prev.includes(id)
        ? prev.filter(x => x !== id)
        : [...prev, id]
    );
  };

  useEffect(() => {
    setSelectedContacts([]);
  }, [selectedTable, selectedGroup, selectedSubgroup, searchTerm, currentPage]);

  const handleSelectAll = () => {
    if (selectedContacts.length === paginatedContacts.length) {
      setSelectedContacts([]);
    } else {
      setSelectedContacts(
        paginatedContacts.map(x => x.id)
      );
    }
  };

  const handleDeleteSelected = async () => {
    if (selectedContacts.length === 0) {
      alert("Please select contacts");
      return;
    }
    if (!window.confirm(`Delete ${selectedContacts.length} contacts?`))
      return;
    const user = JSON.parse(sessionStorage.getItem("user"));
    const userId =
      user.role === "admin"
        ? user.id
        : user.userId;
    try {
      const response = await fetch("https://botapi.emedha.in/pages/api/delete-multiple-contacts",
        {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json"
          },
          body: JSON.stringify({
            ids: selectedContacts,
            user_id: userId
          })
        }
      );
      const result = await response.json();
      alert(result.message);
      setSelectedContacts([]);
      fetchContacts();
    } catch (err) {
      console.log(err);
    }
  };

  // --- Fetch Tables Metadata ---
  const fetchTables = async () => {
    try {
      const user = JSON.parse(sessionStorage.getItem('user'));
      if (!user || !user.id) return;

      const userId = user.role === "admin" ? user.id : user.userId;
      const response = await axios.get('https://botapi.emedha.in/pages/api/api/tables', {
        params: { userId }
      });

      const tableData = response.data || [];
      let filteredTables = tableData;

      if (user.role === "member") {
        filteredTables = tableData.filter(
          (t) => t.role === "member" && t.member_id === user.id
        );
      }

      setTables(filteredTables);
    } catch (err) {
      console.error("Failed to fetch tables:", err);
    }
  };

  // --- Integrated Structural Fetcher (Groups & Subgroups) ---
  const fetchMetadata = async () => {
    try {
      const user = JSON.parse(sessionStorage.getItem('user'));
      if (!user || !user.id) {
        console.error('User not logged in or missing ID');
        return;
      }

      const idToUse = user.role === "admin" ? user.id : user.userId;

      const groupsQueryString = `?userId=${idToUse}`;
      const subgroupsQueryString = `?user_id=${idToUse}`;

      const [groupRes, subgroupRes] = await Promise.all([
        axios.get(`https://botapi.emedha.in/pages/api/api/groups${groupsQueryString}`),
        axios.get(`https://botapi.emedha.in/pages/api/api/subgroups${subgroupsQueryString}`)
      ]);

      let groupsData = groupRes.data || [];
      let subgroupsData = subgroupRes.data || [];

      if (user.role === "member") {
        groupsData = groupsData.filter(
          group => group.role === "member" && String(group.member_id) === String(user.id)
        );
        subgroupsData = subgroupsData.filter(
          sub => sub.role === "member" && String(sub.member_id) === String(user.id)
        );
      }

      setGroups(groupsData);
      setSubgroups(subgroupsData);
    } catch (error) {
      console.error('Error fetching layout metadata segments:', error);
    }
  };

  // --- Dynamic API Fetcher for Contacts ---
  const fetchContacts = async () => {
    try {
      const user = JSON.parse(sessionStorage.getItem('user'));
      if (!user || !user.id) {
        alert("User not found or not logged in.");
        return;
      }

      const url = new URL('https://botapi.emedha.in/pages/api/api/contactslist/user');
      url.searchParams.append('user_id', user.role === "admin" ? user.id : user.userId);

      if (selectedTable) url.searchParams.append('tableName', selectedTable);
      if (selectedGroup) url.searchParams.append('groupName', groupNameMap[selectedGroup] || "");
      if (selectedSubgroup) url.searchParams.append('subgroupName', subgroupNameMap[selectedSubgroup] || "");

      const response = await fetch(url);
      if (!response.ok) throw new Error('Failed to fetch contacts');

      const data = await response.json();

      const rawRows = data.contacts || data.rows || [];
      const tableName = data.table || "";

      const formattedRows = rawRows.map(row => ({
        ...row,
        table_name: row.table_name || tableName
      }));

      setContacts(formattedRows);
    } catch (err) {
      console.error('Error fetching contacts:', err);
      setContacts([]);
    }
  };

  useEffect(() => {
    fetchTables();
    fetchMetadata();
  }, []);

  useEffect(() => {
    fetchContacts();
  }, [selectedTable, selectedGroup, selectedSubgroup]);

  // --- Safe Lookup Maps for Group/Subgroup Names ---
  const groupNameMap = useMemo(() => {
    const map = {};
    groups.forEach(g => {
      const name = g.name || g.group_name || "Unknown Group";
      if (g.id) map[String(g.id).trim()] = name;
      if (g.group_id) map[String(g.group_id).trim()] = name;
    });
    return map;
  }, [groups]);

  const subgroupNameMap = useMemo(() => {
    const map = {};
    subgroups.forEach(sg => {
      const name = sg.name || sg.subgroup_name || "Unknown Subgroup";
      if (sg.id) map[String(sg.id).trim()] = name;
      if (sg.subgroup_id) map[String(sg.subgroup_id).trim()] = name;
    });
    return map;
  }, [subgroups]);

  // Filter groups/subgroups based on selected table if table schema maps them
  const currentGroupOptions = useMemo(() => {
    if (!selectedTable) return groups;
    // Filter groups linked to the selected table if relation exists, otherwise return all
    return groups.filter(g => !g.table_name || g.table_name === selectedTable);
  }, [selectedTable, groups]);

  const currentSubgroupOptions = useMemo(() => {
    if (!selectedGroup) return subgroups;
    return subgroups.filter(sub => String(sub.group_id).trim() === String(selectedGroup).trim());
  }, [selectedGroup, subgroups]);

  // --- UI Action Event Handlers ---
  const handleTableChange = (e) => {
    setSelectedTable(e.target.value);
    setSelectedGroup("");
    setSelectedSubgroup("");
    setCurrentPage(1);
  };

  const handleGroupChange = (e) => {
    setSelectedGroup(e.target.value);
    setSelectedSubgroup("");
    setCurrentPage(1);
  };

  const handleStartEdit = (item) => {
    setEditingContact(item);
    setEditName(item.name || item.contact_name || "");
    setEditMobile(item.mobile || item.contact_mobile || "");

    const extras = {};
    Object.keys(item).forEach(key => {
      if (!excludedKeys.includes(key)) {
        extras[key] = item[key] !== null && item[key] !== undefined ? item[key] : "";
      }
    });
    setEditExtraFields(extras);
  };

  const handleUpdateContact = async (e) => {
    e.preventDefault();
    if (!editingContact?.id) {
      alert('Contact ID is missing!');
      return;
    }

    const user = JSON.parse(sessionStorage.getItem('user'));
    const idToUse = user?.role === "admin" ? user.id : user?.userId;

    try {
      const contactData = {
        name: editName,
        mobile: editMobile,
        groupId: editingContact.group_id || editingContact.groupId,
        subgroupId: editingContact.subgroup_id || editingContact.subgroupId,
        user_id: idToUse,
        table_name: editingContact.table_name || selectedTable || undefined,
        ...editExtraFields
      };

      const response = await fetch(`https://botapi.emedha.in/pages/api/update-contact/${editingContact.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(contactData),
      });
      const result = await response.json();

      if (response.ok) {
        alert('Contact updated successfully');
        fetchContacts();
        setEditingContact(null);
      } else {
        alert(result.error || 'Failed to update contact');
      }
    } catch (err) {
      console.error('Error updating contact:', err);
      alert('Network error while updating contact');
    }
  };

  const handleDelete = async (item) => {
    if (!window.confirm("Delete this contact?")) return;
    const user = JSON.parse(sessionStorage.getItem("user"));
    const userId =
      user.role === "admin"
        ? user.id
        : user.userId;
    try {
      const response = await fetch(`https://botapi.emedha.in/pages/api/delete-contact/${item.id}?user_id=${userId}`,
        {
          method: "DELETE"
        }
      );
      const result = await response.json();
      if (!response.ok) {
        alert(result.message);
        return;
      }
      alert(result.message);
      setContacts(prev =>
        prev.filter(x => x.id !== item.id)
      );
      await fetchContacts();
    } catch (err) {
      console.log(err);
      alert("Delete failed");
    }
  };

  const filteredContacts = useMemo(() => {
    return contacts.filter(item => {
      const textSearch = searchTerm.trim().toLowerCase();
      const name = item.name || '';
      const mobile = item.mobile || '';

      return textSearch === "" ||
        name.toLowerCase().includes(textSearch) ||
        String(mobile).includes(textSearch);
    });
  }, [contacts, searchTerm]);

  const paginatedContacts = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return filteredContacts.slice(startIndex, startIndex + itemsPerPage);
  }, [filteredContacts, currentPage]);

  const totalPages = Math.ceil(filteredContacts.length / itemsPerPage) || 1;

  const styles = {
    wrapper: { fontFamily: 'system-ui, -apple-system, sans-serif', backgroundColor: '#f8fafc', minHeight: '100vh', padding: '24px' },
    header: { display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' },
    title: { fontSize: '20px', fontWeight: '700', color: '#1e293b', margin: 0 },
    subtitle: { fontSize: '13px', color: '#64748b', margin: '0 0 24px 0' },
    gridContainer: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '24px', alignItems: 'start' },
    leftCol: { display: 'flex', flexDirection: 'column', gap: '24px', gridColumn: 'span 2' },
    card: { backgroundColor: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '12px', padding: '20px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' },
    cardTitle: { fontSize: '14px', fontWeight: '600', color: '#334155', marginTop: 0, marginBottom: '16px' },
    filterGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '16px' },
    formGroup: { display: 'flex', flexDirection: 'column', gap: '6px' },
    label: { fontSize: '12px', fontWeight: '600', color: '#475569' },
    input: { width: '100%', fontSize: '13px', padding: '8px 12px', border: '1px solid #cbd5e1', borderRadius: '8px', boxSizing: 'border-box', backgroundColor: '#fff', outline: 'none' },
    searchContainer: { position: 'relative', display: 'flex', alignItems: 'center' },
    tableCard: { backgroundColor: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '12px', overflow: 'hidden', marginTop: '24px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' },
    table: { width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '13px' },
    th: { backgroundColor: '#f8fafc', borderBottom: '1px solid #e2e8f0', color: '#475569', fontWeight: '600', padding: '14px 20px', textTransform: 'uppercase', fontSize: '11px' },
    td: { padding: '16px 20px', borderBottom: '1px solid #f1f5f9', color: '#334155' },
    btnPrimary: { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', backgroundColor: '#3b82f6', color: '#fff', border: 'none', padding: '8px 16px', borderRadius: '8px', fontSize: '13px', fontWeight: '500', cursor: 'pointer', width: '100%' },
    btnSecondary: { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', backgroundColor: '#fff', color: '#64748b', border: '1px solid #e2e8f0', padding: '8px 16px', borderRadius: '8px', fontSize: '13px', fontWeight: '500', cursor: 'pointer' },
    actionBtn: { border: 'none', padding: '6px', borderRadius: '6px', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' },
    footer: { padding: '12px 20px', backgroundColor: '#f8fafc', borderTop: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: '12px', color: '#64748b' }
  };

  return (
    <div style={styles.wrapper}>
      <div style={styles.header}>
        <div style={{ backgroundColor: '#eff6ff', padding: '8px', borderRadius: '8px', color: '#3b82f6', display: 'flex' }}>
          <Users size={18} />
        </div>
        <h1 style={styles.title}>Contact Directory Mapping</h1>
      </div>
      <p style={styles.subtitle}>Select database tables first, drill down into groups and subgroups, and manage contact records dynamically.</p>

      <div style={styles.gridContainer}>
        <div style={styles.leftCol}>
          <div style={styles.card}>
            <h3 style={styles.cardTitle}>Filter & Track Contacts Hierarchy</h3>
            <div style={styles.filterGrid}>

              {/* Table Filter Selector */}
              <div style={styles.formGroup}>
                <label style={styles.label}>Select Table Name</label>
                <select style={styles.input} value={selectedTable} onChange={handleTableChange}>
                  <option value="">All Tables</option>
                  {tables.map((tbl, i) => (
                    <option key={i} value={tbl.table_name}>
                      {tbl.table_name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Group Filter Selector */}
              <div style={styles.formGroup}>
                <label style={styles.label}>Select Group Name</label>
                <select style={{ ...styles.input, opacity: !selectedTable ? 0.6 : 1 }} value={selectedGroup} disabled={!selectedTable} onChange={handleGroupChange}>
                  <option value="">All Groups</option>
                  {currentGroupOptions.map((opt, i) => (
                    <option key={i} value={opt.id || opt.group_id}>
                      {opt.name || opt.group_name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Subgroup Filter Selector */}
              <div style={styles.formGroup}>
                <label style={styles.label}>Select SubGroup Name</label>
                <select style={{ ...styles.input, opacity: !selectedGroup ? 0.6 : 1 }} value={selectedSubgroup} disabled={!selectedGroup} onChange={(e) => { setSelectedSubgroup(e.target.value); setCurrentPage(1); }}>
                  <option value="">All Subgroups</option>
                  {currentSubgroupOptions.map((opt, i) => (
                    <option key={i} value={opt.id || opt.subgroup_id}>
                      {opt.name || opt.subgroup_name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Search Field */}
              <div style={styles.formGroup}>
                <label style={styles.label}>Search Records</label>
                <div style={styles.searchContainer}>
                  <Search size={14} style={{ position: 'absolute', left: '10px', color: '#94a3b8' }} />
                  <input type="text" placeholder="Search name/mobile..." value={searchTerm} onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }} style={{ ...styles.input, paddingLeft: '32px' }} />
                </div>
              </div>

            </div>
          </div>
        </div>

        {/* Edit Panel Column */}
        <div style={{ gridColumn: 'span 1' }}>
          {editingContact ? (
            <div style={styles.card}>
              <h3 style={styles.cardTitle}>Update Details for Entry ID: {editingContact.id}</h3>
              <form onSubmit={handleUpdateContact} style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>

                <div style={styles.formGroup}>
                  <label style={styles.label}>Assigned Table</label>
                  <input type="text" style={{ ...styles.input, backgroundColor: '#f1f5f9', color: '#64748b' }} value={editingContact.table_name || selectedTable || 'N/A'} disabled />
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Assigned Group</label>
                  <input type="text" style={{ ...styles.input, backgroundColor: '#f1f5f9', color: '#64748b' }} value={editingContact.group_name || groupNameMap[String(editingContact.group_id).trim()] || 'N/A'} disabled />
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Assigned Subgroup</label>
                  <input type="text" style={{ ...styles.input, backgroundColor: '#f1f5f9', color: '#64748b' }} value={editingContact.subgroup_name || subgroupNameMap[String(editingContact.subgroup_id).trim()] || 'N/A'} disabled />
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Contact Name</label>
                  <input type="text" style={styles.input} value={editName} onChange={(e) => setEditName(e.target.value)} required />
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Mobile Number</label>
                  <input type="text" style={styles.input} value={editMobile} onChange={(e) => setEditMobile(e.target.value)} required />
                </div>

                {Object.keys(editExtraFields).map((key) => {
                  const val = editExtraFields[key];
                  const lowerKey = key.toLowerCase();
                  const isDate = lowerKey.includes('date') || lowerKey.includes('dob') || lowerKey.includes('created');
                  const isImage = lowerKey.includes('image') || lowerKey.includes('photo') || lowerKey.includes('img') || lowerKey.includes('avatar');
                  const isNumber = lowerKey.includes('amount') || lowerKey.includes('price') || lowerKey.includes('qty') || lowerKey.includes('count') || lowerKey.includes('plot');
                  return (
                    <div key={key} style={styles.formGroup}>
                      <label style={styles.label}>{key.replace(/_/g, ' ').toUpperCase()}</label>

                      {isDate ? (
                        <input type="date" style={styles.input} value={val ? String(val).split('T')[0] : ''} onChange={(e) => setEditExtraFields(prev => ({ ...prev, [key]: e.target.value }))} />
                      ) : isImage ? (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                          {val && (
                            <img src={val.startsWith('http') ? val : `https://botapi.emedha.in/${val}`} alt="Preview" style={{ width: '40px', height: '40px', objectFit: 'cover', borderRadius: '4px', border: '1px solid #cbd5e1' }} />
                          )}
                          <input type="text" placeholder="Enter image URL or path..." style={styles.input} value={val} onChange={(e) => setEditExtraFields(prev => ({ ...prev, [key]: e.target.value }))} />
                        </div>
                      ) : isNumber ? (
                        <input type="number" style={styles.input} value={val} onChange={(e) => setEditExtraFields(prev => ({ ...prev, [key]: e.target.value }))} />
                      ) : (
                        <input type="text" style={styles.input} value={val !== null && val !== undefined ? val : ''} onChange={(e) => setEditExtraFields(prev => ({ ...prev, [key]: e.target.value }))} />
                      )}
                    </div>
                  );
                })}
                <div style={{ display: 'flex', gap: '8px', marginTop: '6px' }}>
                  <button type="submit" style={styles.btnPrimary}><Save size={14} /> Save Changes</button>
                  <button type="button" onClick={() => setEditingContact(null)} style={styles.btnSecondary}><X size={14} /> Cancel</button>
                </div>
              </form>
            </div>
          ) : (
            <div style={{ ...styles.card, borderStyle: 'dashed', backgroundColor: '#f8fafc', textAlign: 'center', padding: '48px 20px' }}>
              <p style={{ fontSize: '13px', fontWeight: '500', color: '#64748b', margin: 0 }}>No record selected.</p>
              <p style={{ fontSize: '11px', color: '#94a3b8', marginTop: '6px' }}>Click the edit pen tool on any row to load adjustments fields.</p>
            </div>
          )}
        </div>
      </div>

      {selectedContacts.length > 0 && (
        <div style={{ marginTop: 20, display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 15px", background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 8 }}>
          <span style={{ fontWeight: 600, color: "#991b1b" }}>
            {selectedContacts.length} contact(s) selected
          </span>
          <button onClick={handleDeleteSelected} style={{ background: "#dc2626", color: "#fff", border: "none", padding: "8px 16px", borderRadius: 6, cursor: "pointer", display: "flex", alignItems: "center", gap: 6 }}>
            <Trash2 size={16} />Delete ({selectedContacts.length})</button>
        </div>
      )}

      {/* Contacts Table */}
      <div style={styles.tableCard}>
        <div style={{ overflowX: 'auto' }}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}><input type="checkbox" checked={paginatedContacts.length > 0 && selectedContacts.length === paginatedContacts.length} onChange={handleSelectAll} /></th>
                <th style={styles.th}>Full Name</th>
                <th style={styles.th}>Mobile Number</th>
                <th style={styles.th}>Table Name</th>
                <th style={styles.th}>Group Name</th>
                <th style={styles.th}>SubGroup Name</th>
                <th style={{ ...styles.th, textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {paginatedContacts.length > 0 ? (
                paginatedContacts.map((item) => {
                  const isCurrentTarget = editingContact?.id === item.id;
                  return (
                    <tr key={item.id} style={{ backgroundColor: isCurrentTarget ? '#f0fdf4' : 'transparent' }}>
                      <td style={styles.td}><input type="checkbox" checked={selectedContacts.includes(item.id)} onChange={() => handleCheckbox(item.id)} /></td>
                      <td style={{ ...styles.td, fontWeight: '500' }}>{item.name}</td>
                      <td style={{ ...styles.td, fontFamily: 'monospace' }}>{item.mobile}</td>
                      <td style={styles.td}><code style={{ fontSize: '11px', padding: '2px 6px', backgroundColor: '#f1f5f9', borderRadius: '4px' }}>{item.table_name || selectedTable || 'N/A'}</code></td>
                      <td style={styles.td}><span style={{ fontSize: '12px', padding: '4px 8px', backgroundColor: '#eff6ff', borderRadius: '6px', color: '#2563eb' }}>{item.group_name || groupNameMap[String(item.group_id).trim()] || 'N/A'}</span></td>
                      <td style={styles.td}><span style={{ fontSize: '12px', padding: '4px 8px', backgroundColor: '#f1f5f9', borderRadius: '6px', color: '#475569' }}>{item.subgroup_name || subgroupNameMap[String(item.subgroup_id).trim()] || 'N/A'}</span></td>
                      <td style={{ ...styles.td, textAlign: 'right' }}>
                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '6px' }}>
                          <button onClick={() => handleStartEdit(item)} style={{ ...styles.actionBtn, backgroundColor: isCurrentTarget ? '#3b82f6' : '#eff6ff', color: isCurrentTarget ? '#fff' : '#2563eb' }}><Edit2 size={13} /></button>
                          <button onClick={() => handleDelete(item)} style={{ ...styles.actionBtn, backgroundColor: '#fff5f5', color: '#e11d48' }}><Trash2 size={13} /></button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={7} style={{ ...styles.td, textAlign: 'center', padding: '32px', color: '#94a3b8' }}>
                    No contacts found matching specified filters.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div style={styles.footer}>
          <div>
            Showing <strong>{filteredContacts.length === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1}</strong> to{" "}
            <strong>{Math.min(currentPage * itemsPerPage, filteredContacts.length)}</strong> of{" "}
            <strong>{filteredContacts.length}</strong> entries
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <button onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))} disabled={currentPage === 1} style={{ ...styles.btnSecondary, padding: '4px 8px', opacity: currentPage === 1 ? 0.5 : 1 }}>
              <ChevronLeft size={14} />
            </button>
            <span style={{ minWidth: '80px', textAlign: 'center' }}>Page {currentPage} of {totalPages}</span>
            <button onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))} disabled={currentPage === totalPages} style={{ ...styles.btnSecondary, padding: '4px 8px', opacity: currentPage === totalPages ? 0.5 : 1 }}>
              <ChevronRight size={14} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GroupContactManager;